<template>
    <p class="subheadline">Zahlweise</p>
    <div class="select">
        <select required :value="paymentMethod" @change="selectPaymentMethod">
            <option v-if="allowedPaymentMethods.length > 1" disabled value="">Bitte auswählen</option>
            <option v-for="method in allowedPaymentMethods" :key="method" :value="method">
                {{ getNameForPaymentMethod(method) }}
            </option>
        </select>
    </div>
    <div v-if="showBankAccountInfo">
        <p class="subheadline">Bankverbindung</p>
        <div>
            <label for="bank-accountOwnerFirstName" class="small">Vorname (Kontoinhaber)*</label>
            <input id="bank-accountOwnerFirstName" class="input" type="text" placeholder="Vorname (Kontoinhaber)" required
                   :value="bankAccountOwnerFirstName"
                   @input="updateBankAccountOwner($event.target.value, bankAccountOwnerLastName)">
        </div>
        <div>
            <label for="bank-accountOwnerLastName" class="small">Nachname (Kontoinhaber)*</label>
            <input id="bank-accountOwnerLastName" class="input" type="text" placeholder="Nachname (Kontoinhaber)" required
                   :value="bankAccountOwnerLastName"
                   @input="updateBankAccountOwner(bankAccountOwnerFirstName, $event.target.value)">
        </div>
        <div>
            <label for="bank-bankName" class="small">Name der Bank*</label>
            <input id="bank-bankName" class="input" type="text" placeholder="Name der Bank" required
                   :value="bankName"
                   @input="$emit('update:bankName', $event.target.value)">
        </div>
        <div>
            <label for="bank-bic" class="small">BIC*</label>
            <input id="bank-bic" class="input" type="text" placeholder="BIC" required
                   :class="{'is-danger':bicError}"
                   :value="bankBic"
                   @input="$emit('update:bankBic', $event.target.value)"
                   @blur="validateBic">
            <span class="is-size-7 has-text-danger" v-if="bicError">{{ bicError }}</span>
        </div>
        <div>
            <label for="bank-iban" class="small">IBAN*</label>
            <input id="bank-iban" class="input" type="text" placeholder="IBAN" required
                   :class="{'is-danger':ibanError}"
                   :value="bankIban"
                   @input="$emit('update:bankIban', $event.target.value)"
                   @blur="validateIban">
            <span class="is-size-7 has-text-danger" v-if="ibanError">{{ ibanError }}</span>
        </div>
    </div>
</template>

<script>
import { getNameForPaymentType, paymentMethodRequiresBankAccountInfo } from '@/utils/registration';
import bic from 'bic'
import iban from 'iban'

export default {
    name: 'RegisterFormPartPayment',
    props: {
        allowedPaymentMethods: Array,
        paymentMethod: String,
        bankAccountOwner: String,
        bankName: String,
        bankBic: String,
        bankIban: String,
        bicError: String,
        ibanError: String,
    },
    emits: [
        'update:paymentMethod',
        'update:bankAccountOwner',
        'update:bankName',
        'update:bankBic',
        'update:bankIban',
        'update:bicError',
        'update:ibanError',
    ],
    data() {
        return {
            bankAccountOwnerFirstName: '',
            bankAccountOwnerLastName: '',
        };
    },
    computed: {
        showBankAccountInfo() {
            return paymentMethodRequiresBankAccountInfo(this.paymentMethod);
        }
    },
    mounted() {
        this.autoSelectSinglePaymentMethod();
    },
    watch: {
        allowedPaymentMethods: {
            handler() {
                this.autoSelectSinglePaymentMethod();
            },
            immediate: true
        },
        bankAccountOwner: {
            handler(newValue) {
                const normalized = (newValue || '').trim();
                const combined = this.combineBankAccountOwner(
                    this.bankAccountOwnerFirstName,
                    this.bankAccountOwnerLastName
                );
                if (normalized === combined) {
                    return;
                }

                const parsed = this.parseBankAccountOwner(newValue);
                this.bankAccountOwnerFirstName = parsed.firstName;
                this.bankAccountOwnerLastName = parsed.lastName;
            },
            immediate: true
        }
    },
    methods: {
        getNameForPaymentMethod(paymentMethod) {
            return getNameForPaymentType(paymentMethod);
        },
        parseBankAccountOwner(bankAccountOwner) {
            const normalized = (bankAccountOwner || '').trim();
            if (!normalized) {
                return { firstName: '', lastName: '' };
            }

            const parts = normalized.split(/\s+/);
            if (parts.length === 1) {
                return { firstName: parts[0], lastName: '' };
            }

            return {
                firstName: parts[0],
                lastName: parts.slice(1).join(' ')
            };
        },
        combineBankAccountOwner(firstName, lastName) {
            return [firstName, lastName]
                .map((value) => value?.trim())
                .filter(Boolean)
                .join(' ');
        },
        updateBankAccountOwner(firstName, lastName) {
            this.bankAccountOwnerFirstName = firstName;
            this.bankAccountOwnerLastName = lastName;
            const combined = this.combineBankAccountOwner(firstName, lastName);
            this.$emit('update:bankAccountOwner', combined);
        },
        validateBic() {
            let error = ''
            if (false === bic.isValid(this.bankBic)) {
                error = 'Ungültige BIC'
            }

            this.$emit('update:bicError', error)
        },
        validateIban() {
            let error = ''
            if (false === iban.isValid(this.bankIban)) {
                error = 'Ungültige IBAN'
            }

            this.$emit('update:ibanError', error)
        },
        selectPaymentMethod(event) {
            const method = event.target.value;
            this.$emit('update:paymentMethod', method);
            if (!paymentMethodRequiresBankAccountInfo(method)) {
                this.$emit('update:bankAccountOwner', '');
                this.$emit('update:bankName', '');
                this.$emit('update:bankBic', '');
                this.$emit('update:bankIban', '');
                this.$emit('update:bicError', '');
                this.$emit('update:ibanError', '');
            }
        },
        autoSelectSinglePaymentMethod() {
            // Auto-select payment method if only one is available and none is currently selected
            if (this.allowedPaymentMethods.length === 1 && !this.paymentMethod) {
                this.$emit('update:paymentMethod', this.allowedPaymentMethods[0]);
            }
        }
    }
}
</script>
